import api from '@/api';
import isEmpty from 'lodash/isEmpty';
import pusherService, { PUSHER_USAGE_TYPES } from '@/utils/pusher';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import { SET_INPUT_CONFIG, SET_IS_UPDATING_SUBFLOW_PARAMS } from './mutations';
import { subflows } from './getters';

import { setAllWorkflowData, deleteWorkflowFromStore } from '../workflows/actions';
import { handleWorkflowUpdated, fetchIsWaitingForSampleData } from '../workflows/actions';
import { nodesData } from '../selectedWorkflow/getters';
import { updateNodeTestResult, updateNode } from '../nodes/actions';

import * as WorkflowNodeService from '@/utils/workflow-node-service';
const buildSubflowsActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const fetchSubflows = buildSubflowsActionObject('fetchSubflows');
export const fetchSubflowParam = buildSubflowsActionObject('fetchSubflowParam');
export const updateSubflowParam = buildSubflowsActionObject('updateSubflowParam');
export const createWorkflowPusherConnection = buildSubflowsActionObject('createWorkflowPusherConnection');
export const removeWorkflowPusherConnection = buildSubflowsActionObject('removeWorkflowPusherConnection');
export const setIsUpdatingSubflowParams = buildSubflowsActionObject('setIsUpdatingSubflowParams');
export const deleteParamsMapParamsFromAllEndNodes = buildSubflowsActionObject('deleteParamsMapParamsFromAllEndNodes');
export const setNeedToTestAllNodes = buildSubflowsActionObject('setNeedToTestAllNodes');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [fetchSubflows.action]({ dispatch, getters }, payload) {
		const { projectId } = payload;
		const newSubflows = await api.getSubflows(projectId);
		const subflowsIds = await newSubflows.map(({ id }) => id);
		const existingSubflowsIds = getters[subflows.getter].map(({ id }) => id);
		const subflowIdsToDelete = existingSubflowsIds.filter((id) => !subflowsIds.includes(id));
		await Promise.all(newSubflows.map((subflow) => dispatch(setAllWorkflowData.fullPath, { data: subflow }, { root: true })));
		await Promise.all(
			subflowIdsToDelete.map((workflowId) => dispatch(deleteWorkflowFromStore.fullPath, { id: workflowId }, { root: true }))
		);
	},
	async [fetchSubflowParam.action]({ commit, state }, payload) {
		const { subflowParams } = state;
		const needToGet = isEmpty(subflowParams) || payload?.forceGet;
		if (needToGet) {
			const params = await api.getSubflowParam(payload.id);
			commit(SET_INPUT_CONFIG, { id: payload.id, params });
		}
	},

	async [updateSubflowParam.action]({ commit, dispatch }, { id, subflowParams }) {
		dispatch(setIsUpdatingSubflowParams.action, true);
		const response = await api.updateSubflowParams(id, subflowParams);
		commit(SET_INPUT_CONFIG, { id: response.id, params: response.params });
		dispatch(setIsUpdatingSubflowParams.action, false);
	},
	[createWorkflowPusherConnection.action]({ dispatch }, { subFlowId }) {
		const workflowId = Number(subFlowId);
		pusherService.listenToEvents(
			[
				pusherService.createPusherEventObj(
					'workflow-updated',
					async (data) => {
						dispatch(
							handleWorkflowUpdated.fullPath,
							{ ...data, getSubFlowConfigurationParams: true, getWorkflowWaitingFlag: true },
							{ root: true }
						);
					},
					PUSHER_USAGE_TYPES.WORKFLOW
				),
				pusherService.createPusherEventObj(
					'workflow-waiting-flag-updated',
					async (data) => dispatch(fetchIsWaitingForSampleData.fullPath, { ...data }, { root: true }),
					PUSHER_USAGE_TYPES.WORKFLOW
				),
			],
			workflowId
		);
		pusherService.subscribeToWorkflow(workflowId);
	},
	/* eslint-disable no-empty-pattern */
	[removeWorkflowPusherConnection.action]({}, { subFlowId }) {
		pusherService.clearWorkflowChannel(false, Number(subFlowId));
	},
	[setIsUpdatingSubflowParams.action]({ commit }, payload) {
		commit(SET_IS_UPDATING_SUBFLOW_PARAMS, payload);
	},
	async [deleteParamsMapParamsFromAllEndNodes.action]({ rootGetters, dispatch }, payload) {
		const { deletedParams } = payload;
		const nodes = rootGetters[nodesData.fullPath];
		await Promise.all(
			Object.entries(nodes).map(async ([nodeId, node]) => {
				if (WorkflowNodeService.isEndRun(node)) {
					deletedParams.forEach((param) => {
						delete node.params_map[param];
					});
					await dispatch(updateNode.fullPath, { node }, { root: true });
					await dispatch(
						updateNodeTestResult.fullPath,
						{ id: nodeId, testResponse: { test_result_status: TEST_RESULT_STATUSES.UNKNOWN } },
						{ root: true }
					);
				}
			})
		);
	},
	async [setNeedToTestAllNodes.action]({ dispatch, rootGetters }) {
		const endRunNodeIds = Object.values(rootGetters[nodesData.fullPath])
			.filter((n) => WorkflowNodeService.isEndRun(n))
			.map((n) => n.id);
		await Promise.all(
			endRunNodeIds.map((nodeId) =>
				dispatch(
					updateNodeTestResult.fullPath,
					{ id: nodeId, testResponse: { test_result_status: TEST_RESULT_STATUSES.UNKNOWN } },
					{ root: true }
				)
			)
		);
	},
};

export default actions;
